import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trimsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Trash2, Upload, Image, Clock, AlertTriangle,
  CirclePlus, Minus, Package
} from 'lucide-react';

const TRIM_TYPE_LABELS = {
  button: 'Button', thread: 'Thread', lining: 'Lining', other: 'Other',
};

const COLOUR_MAP = {
  'dark brown': '#5C3317', 'black': '#1a1a1a', 'white': '#F5F5F5', 'smoke grey': '#8B8B8B',
  'navy': '#1B2A4A', 'gold': '#C5A55A', 'burgundy': '#722F37', 'charcoal': '#36454F',
  'natural': '#D2B48C',
};

function getColourHex(colourName) {
  if (!colourName) return '#D1D5DB';
  const lower = colourName.toLowerCase();
  for (const [key, hex] of Object.entries(COLOUR_MAP)) {
    if (lower.includes(key)) return hex;
  }
  return '#D1D5DB';
}

function formatDateTime(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return isoStr; }
}

export default function TrimsFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === 'new';
  const canEdit = user?.role === 'Admin';

  const [trim, setTrim] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', trim_type: 'button', material: '', colour: '', size: '',
    cost_price: '', unit: 'piece', stock_quantity: '0', min_stock_alert: '10',
    is_active: true, notes: '',
  });

  const fetchTrim = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const res = await trimsAPI.get(id);
      setTrim(res.data);
      setForm({
        name: res.data.name,
        trim_type: res.data.trim_type,
        material: res.data.material || '',
        colour: res.data.colour || '',
        size: res.data.size || '',
        cost_price: String(res.data.cost_price || ''),
        unit: res.data.unit || 'piece',
        stock_quantity: String(res.data.stock_quantity || 0),
        min_stock_alert: String(res.data.min_stock_alert || 10),
        is_active: res.data.is_active,
        notes: res.data.notes || '',
      });
    } catch (err) {
      toast.error('Failed to load trim');
      navigate('/app/trims');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => { fetchTrim(); }, [fetchTrim]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.cost_price) { toast.error('Cost price is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        trim_type: form.trim_type,
        material: form.material || null,
        colour: form.colour || '',
        size: form.size || null,
        cost_price: parseFloat(form.cost_price),
        unit: form.unit,
        stock_quantity: parseInt(form.stock_quantity) || 0,
        min_stock_alert: parseInt(form.min_stock_alert) || 10,
        is_active: form.is_active,
        notes: form.notes || null,
      };
      if (isNew) {
        const res = await trimsAPI.create(payload);
        toast.success('Trim created');
        navigate(`/app/trims/${res.data.id}`, { replace: true });
      } else {
        await trimsAPI.update(id, payload);
        toast.success('Trim updated');
        fetchTrim();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this trim? This cannot be undone.')) return;
    try {
      await trimsAPI.delete(id);
      toast.success('Trim deleted');
      navigate('/app/trims');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await trimsAPI.uploadImage(id, formData);
      toast.success('Image uploaded');
      fetchTrim();
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  if (loading) {
    return (<div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>);
  }

  const colourHex = getColourHex(form.colour);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-6" data-testid="trims-form-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/trims')}
            className="h-9 px-2" data-testid="back-to-trims">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
          </Button>
          <div>
            <h1 className="text-xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              {isNew ? 'New Trim' : form.name}
            </h1>
            {!isNew && trim && (
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded-md">
                  {TRIM_TYPE_LABELS[trim.trim_type]}
                </Badge>
                {trim.is_low_stock && (
                  <Badge className="text-[10px] px-1.5 py-0 rounded-md" style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#DC2626', border: 'none' }}>
                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Low Stock
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
        {!isNew && canEdit && (
          <Button variant="ghost" onClick={handleDelete} className="h-9 rounded-lg text-xs" style={{ color: 'var(--danger)' }}
            data-testid="delete-trim-button">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Name *</Label>
                  <Input data-testid="trim-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" placeholder="e.g. Horn Button - Dark Brown 20mm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Type</Label>
                  <Select value={form.trim_type} onValueChange={v => setForm(p => ({...p, trim_type: v}))} disabled={!canEdit && !isNew}>
                    <SelectTrigger className="h-10 rounded-lg text-sm" data-testid="trim-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="button">Button</SelectItem>
                      <SelectItem value="thread">Thread</SelectItem>
                      <SelectItem value="lining">Lining</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Material</Label>
                  <Input data-testid="trim-material-input" value={form.material}
                    onChange={e => setForm(p => ({...p, material: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" placeholder="e.g. Horn, Silk, Cupro" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Colour</Label>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 border" style={{ backgroundColor: colourHex, borderColor: `${colourHex}40` }} />
                    <Input data-testid="trim-colour-input" value={form.colour}
                      onChange={e => setForm(p => ({...p, colour: e.target.value}))}
                      disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" placeholder="e.g. Dark Brown, Navy" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Size</Label>
                  <Input data-testid="trim-size-input" value={form.size}
                    onChange={e => setForm(p => ({...p, size: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" placeholder="e.g. 20mm, 100m spool" />
                </div>
              </div>

              <Separator className="opacity-60" />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Cost Price (INR) *</Label>
                  <Input data-testid="trim-cost-input" type="number" value={form.cost_price}
                    onChange={e => setForm(p => ({...p, cost_price: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" placeholder="45" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Unit</Label>
                  <Select value={form.unit} onValueChange={v => setForm(p => ({...p, unit: v}))} disabled={!canEdit && !isNew}>
                    <SelectTrigger className="h-10 rounded-lg text-sm" data-testid="trim-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="piece">Piece</SelectItem>
                      <SelectItem value="metre">Metre</SelectItem>
                      <SelectItem value="spool">Spool</SelectItem>
                      <SelectItem value="pair">Pair</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Stock Qty</Label>
                  <Input data-testid="trim-stock-input" type="number" value={form.stock_quantity}
                    onChange={e => setForm(p => ({...p, stock_quantity: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Min Alert Level</Label>
                  <Input data-testid="trim-min-alert-input" type="number" value={form.min_stock_alert}
                    onChange={e => setForm(p => ({...p, min_stock_alert: e.target.value}))}
                    disabled={!canEdit && !isNew} className="h-10 rounded-lg text-sm" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({...p, is_active: v}))} disabled={!canEdit && !isNew} />
                <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Active</Label>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))}
                  disabled={!canEdit && !isNew} className="h-20 rounded-lg text-sm resize-none" placeholder="Additional notes..." />
              </div>

              {(canEdit || isNew) && (
                <div className="flex justify-end pt-2">
                  <Button data-testid="save-trim-button" onClick={handleSave} disabled={saving}
                    className="h-10 rounded-xl px-6 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                    <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : isNew ? 'Create Trim' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: Image + Stock History */}
        <div className="space-y-6">
          {/* Image */}
          {!isNew && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Image</CardTitle>
              </CardHeader>
              <CardContent>
                {trim?.image_url ? (
                  <img src={`${backendUrl}${trim.image_url}`} alt={trim.name}
                    className="w-full h-40 object-cover rounded-lg mb-3" />
                ) : (
                  <div className="w-full h-40 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${colourHex}12` }}>
                    <div className="w-16 h-16 rounded-full" style={{ backgroundColor: colourHex, opacity: 0.4 }} />
                  </div>
                )}
                {canEdit && (
                  <label className="flex items-center justify-center gap-2 h-9 rounded-lg border border-border/60 cursor-pointer hover:bg-[color:var(--surface-2)] text-xs"
                    style={{ transition: 'background-color 150ms' }}>
                    <Upload className="w-3.5 h-3.5" /> Upload Image
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </label>
                )}
              </CardContent>
            </Card>
          )}

          {/* Stock History */}
          {!isNew && trim?.stock_history && trim.stock_history.length > 0 && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Stock History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[...trim.stock_history].reverse().map((entry, i) => (
                  <div key={entry.id || i} className="text-[11px] p-2.5 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-semibold tabular-nums" style={{
                        color: entry.adjustment > 0 ? '#059669' : '#DC2626'
                      }}>
                        {entry.adjustment > 0 ? '+' : ''}{entry.adjustment}
                      </span>
                      <span className="tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                        {entry.previous_qty} {'\u2192'} {entry.new_qty}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" style={{ color: 'var(--ink-muted)' }} />
                      <span style={{ color: 'var(--ink-muted)' }}>
                        {formatDateTime(entry.adjusted_at)} {'\u00b7'} {entry.adjusted_by}
                      </span>
                    </div>
                    {entry.reason && (
                      <p className="mt-0.5" style={{ color: 'var(--navy-ink)' }}>{entry.reason}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
